import PropTypes from "prop-types";

import { Dialog, DialogContent, DialogActions, Grid, Typography, Alert } from "@mui/material";
import styled from "@emotion/styled";

import HubDialogActionButton from "./HubDialogActionButton";
import { forwardRef } from "react";

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 1.2rem;
  color: #8f97aa;
  cursor: pointer;
  transition: 300ms;

  &:hover {
    color: black;
  }
`;

const HubDialog = forwardRef(function (props, dialogRef) {
  const {
    open = false,
    onClose,
    title,
    subtitle,
    actions,
    maxWidth,
    fullWidth = true,
    fullScreen = false,
    contentSx,
    buttonOnlyClose,
    customActions,
    header,
    contentRef,
  } = props;

  const actionsHeight = props.actionsHeight || (actions ? (actions.alert ? 80 : 60) : 0);

  const handleClose = (event, reason) => {
    if (buttonOnlyClose && reason === "backdropClick") {
      return;
    }

    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      disableEscapeKeyDown={buttonOnlyClose}
      ref={dialogRef}
    >
      <Grid container p="10px 20px" justifyContent="space-between" alignItems="center" columnSpacing={5} bgcolor="#fbfbfb" width="100%">
        {!header && (title || subtitle) && (
          <Grid size={{xs: "grow"}}>
            {title && typeof title === "string" && <Typography variant="h5">{title}</Typography>}
            {title && typeof title !== "string" && title}

            {subtitle && <Typography variant="subtitle1">{subtitle}</Typography>}
          </Grid>
        )}

        <Grid size={{xs: "grow"}}>{header && header}</Grid>

        {!buttonOnlyClose && onClose && (
          <Grid>
            <CloseButton onClick={onClose}>X</CloseButton>
          </Grid>
        )}
      </Grid>

      <DialogContent ref={contentRef} sx={{ height: `calc(100% - ${actionsHeight}px)`, ...contentSx }}>
        {props.children}
      </DialogContent>

      {actions && !customActions && (
        <DialogActions sx={{ height: actionsHeight, ...actions.sx }}>
          <Grid container alignItems="center" justifyContent="end" spacing={3} p="0 15px 10px 15px" sx={actions.sx} width="100%">
            {actions.alert && (
              <Grid size={{xs: "grow"}}>
                <Alert severity={actions.alert.type}>{actions.alert.message}</Alert>{" "}
              </Grid>
            )}

            {actions.message && (
              <Grid size={{xs: "grow"}}>
                <Typography variant="label">{actions.message}</Typography>
              </Grid>
            )}

            {actions.buttons && (
              <Grid>
                <Grid container columnSpacing={2} justifyContent="end" width="100%">
                  {actions.buttons.map((buttonProps, index) => (
                    <Grid key={index}>
                      <HubDialogActionButton {...buttonProps} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogActions>
      )}

      {!actions && customActions && customActions}
    </Dialog>
  );
});

HubDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  subtitle: PropTypes.string,
  actions: PropTypes.shape({
    sx: PropTypes.object,
    alert: PropTypes.shape({
      type: PropTypes.string,
      message: PropTypes.string.isRequired,
    }),
    message: PropTypes.string,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        variant: PropTypes.string,
        size: PropTypes.string,
        loading: PropTypes.bool,
        disabled: PropTypes.bool,
        color: PropTypes.string,
        sx: PropTypes.object,
        label: PropTypes.string,
        onClick: PropTypes.func.isRequired,
      })
    ),
  }),
  actionsHeight: PropTypes.number,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fullScreen: PropTypes.bool,
  fullWidth: PropTypes.bool,
  contentSx: PropTypes.object,
  header: PropTypes.node,
  buttonOnlyClose: PropTypes.bool,
  customActions: PropTypes.node,
  contentRef: PropTypes.object,
};

export default HubDialog;
